import { useEffect, useState } from 'react';

import { lottieAnimations } from '@backrs_ui/common-ui';

import { MemberProfileInfoCard } from '@backrs_ui/grow-ui/src/components/MemberProfileInfoCard/MemberProfileInfoCard';
import {
  selectMemberProfileSummary,
  setMemberProfileSummary,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard/memberProfileSummary';
import { useStreamProfileSummaryMutation } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, type SxProps, type Theme } from '@mui/material';
import Lottie from 'lottie-react';

import { MemberTabErrorState } from './MemberTabErrorState';
import { useCurrentMember } from './useCurrentMember';

export const MemberProfileTab = () => {
  const currentMember = useCurrentMember();
  const {
    member,
    isCoach,
    canViewCoaches,
    canManageCoaches,
    isLoading,
    isError: isMemberError,
  } = currentMember;

  const dispatch = useAppDispatch();
  const memberId = member?.id;
  const cachedSummary = useAppSelector(selectMemberProfileSummary(memberId));
  const [streamedSummary, setStreamedSummary] = useState<string>('');
  const [streamProfileSummary, { isLoading: isSummaryLoading, error: summaryError }] =
    useStreamProfileSummaryMutation();

  useEffect(() => {
    if (!memberId || !isCoach || cachedSummary !== undefined) return;

    setStreamedSummary('');
    const promise = streamProfileSummary({
      user_id: memberId,
      onChunk: (chunk: string) => setStreamedSummary(chunk),
    });

    promise
      .unwrap()
      .then((summary) => dispatch(setMemberProfileSummary({ memberId, summary })))
      .catch(() => undefined);

    return () => {
      promise.abort();
    };
  }, [memberId, isCoach, cachedSummary, streamProfileSummary, dispatch]);

  const profileSummary = cachedSummary ?? streamedSummary;

  if (isMemberError) return <MemberTabErrorState {...currentMember} />;

  if (isLoading) {
    return (
      <Box sx={loadingSx}>
        <Lottie animationData={lottieAnimations.dotsDotting} loop style={{ width: 120 }} />
      </Box>
    );
  }
  if (!member) return null;

  return (
    <Box sx={containerSx}>
      <MemberProfileInfoCard
        member={member}
        profileSummary={profileSummary}
        isSummaryLoading={isSummaryLoading}
        summaryError={!!summaryError}
        canViewCoaches={canViewCoaches}
        canManageCoaches={canManageCoaches}
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  padding: `${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-none']}`,
});

const loadingSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
  py: 8,
};
